<template>
  <div>
    <router-view :key="$route.fullPath"></router-view>
    <FloatingCartButton />
    <ChatDock />
    <NotificationBell v-if="showGlobalBell" />

    <button v-if="showThemeToggle" class="glb-theme-btn" :title="themeDark ? 'Switch to light mode' : 'Switch to dark mode'" @click="toggleGlobalTheme">
      <i class="mdi" :class="themeDark ? 'mdi-white-balance-sunny' : 'mdi-moon-waning-crescent'"></i>
    </button>

    <!-- ===== GLOBAL MOBILE BOTTOM TAB BAR =====
         Same five tabs as Dashboard's tab bar (Home / Market / Farm / Chat / Menu) on
         every logged-in route on mobile. Hidden on /dashboard (its inline version
         already shows these), on the public/auth screens, and on full-screen flows
         that already have their own bottom action bar. -->
    <nav v-if="showGlobalTabbar" class="glb-tabbar d-md-none">
      <a href="javascript:void(0)" class="glb-tab" :class="{active:isActive('/dashboard')}" @click="go('/dashboard')">
        <i class="mdi mdi-view-dashboard"></i><span>Home</span>
      </a>
      <a href="javascript:void(0)" class="glb-tab" :class="{active:isActive('/marketplace')}" @click="go('/marketplace')">
        <i class="mdi mdi-storefront-outline"></i><span>Market</span>
      </a>
      <a href="javascript:void(0)" class="glb-tab" :class="{active:isActive('/farm')}" @click="go('/farm')">
        <i class="mdi mdi-barn"></i><span>Farm</span>
      </a>
      <a href="javascript:void(0)" class="glb-tab" :class="{active:isActive('/forum')}" @click="go('/forum')">
        <i class="mdi mdi-forum-outline"></i><span>Chat</span>
        <span v-if="globalUnread > 0" class="glb-tab-badge">{{ globalUnread > 99 ? '99+' : globalUnread }}</span>
      </a>
      <a href="javascript:void(0)" class="glb-tab" :class="{active:showGlobalMenu}" @click="showGlobalMenu=!showGlobalMenu">
        <i class="mdi mdi-menu"></i><span>Menu</span>
      </a>
    </nav>

    <!-- ===== GLOBAL MENU DRAWER (bottom-sheet on mobile, popped from the Menu tab) ===== -->
    <transition name="glb-menu-fade">
      <div v-if="showGlobalMenu" class="glb-menu-overlay" @click.self="showGlobalMenu=false">
        <div class="glb-menu-sheet">
          <div class="glb-menu-handle"></div>
          <div class="glb-menu-head">
            <strong>{{ userName || 'Menu' }}</strong>
            <button class="glb-menu-close" @click="showGlobalMenu=false"><i class="mdi mdi-close"></i></button>
          </div>
          <a href="javascript:void(0)" class="glb-menu-item" @click="goAndCloseMenu('/me')"><i class="mdi mdi-account-edit"></i>My Profile</a>
          <a href="javascript:void(0)" class="glb-menu-item" @click="goAndCloseMenu('/notifications')"><i class="mdi mdi-bell-outline"></i>Notifications<span v-if="globalUnread>0" class="glb-menu-pill">{{ globalUnread > 99 ? '99+' : globalUnread }}</span></a>
          <a href="javascript:void(0)" class="glb-menu-item" @click="goAndCloseMenu('/my-listings')"><i class="mdi mdi-tag-multiple"></i>My Listings</a>
          <a href="javascript:void(0)" class="glb-menu-item" @click="goAndCloseMenu('/my-offers')"><i class="mdi mdi-handshake"></i>My Offers</a>
          <a href="javascript:void(0)" class="glb-menu-item" @click="goAndCloseMenu('/orders')"><i class="mdi mdi-receipt"></i>My Orders</a>
          <a href="javascript:void(0)" class="glb-menu-item" @click="goAndCloseMenu('/cart')"><i class="mdi mdi-cart"></i>Cart</a>
          <a href="javascript:void(0)" class="glb-menu-item" @click="goAndCloseMenu('/my-corporate-clients')"><i class="mdi mdi-domain"></i>Corporate Clients</a>
          <a href="javascript:void(0)" class="glb-menu-item" @click="goAndCloseMenu('/receivables')"><i class="mdi mdi-cash-multiple"></i>Receivables</a>
          <a href="javascript:void(0)" class="glb-menu-item" @click="goAndCloseMenu('/forum')"><i class="mdi mdi-account-group"></i>Community</a>
          <a href="javascript:void(0)" class="glb-menu-item" @click="goAndCloseMenu('/verification')"><i class="mdi mdi-shield-check"></i>Get Verified</a>
          <div class="glb-menu-divider"></div>
          <a href="javascript:void(0)" class="glb-menu-item glb-menu-danger" @click="logout"><i class="mdi mdi-logout"></i>Sign Out</a>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import FloatingCartButton from "@/components/FloatingCartButton.vue";
import ChatDock from "@/components/flockr-messaging/ChatDock.vue";
import NotificationBell from "@/components/flockr-messaging/NotificationBell.vue";
import { executeJson, flocker } from "@/api";
export default {
  components: { FloatingCartButton, ChatDock, NotificationBell },
  data(){return{
    globalUnread: 0,
    pollTimer: null,
    loggedInTick: !!sessionStorage.getItem('userId'),
    showGlobalMenu: false,
    userName: sessionStorage.getItem('userName') || '',
    themeDark: (document.documentElement.getAttribute('data-theme') || localStorage.getItem('flockr_theme') || 'light') === 'dark',
    themeObserver: null,
  }},
  watch:{
    // sessionStorage isn't reactive — recheck on each navigation so the bar shows/hides
    // immediately after login/logout. Close menu on every route change.
    '$route'(){
      this.loggedInTick = !!sessionStorage.getItem('userId');
      this.userName = sessionStorage.getItem('userName') || '';
      this.showGlobalMenu = false;
      if (this.loggedInTick) this.fetchUnread();
    }
  },
  computed:{
    loggedIn(){ return this.loggedInTick; },
    // Generic predicate used by both bell + global tabbar so the route lists stay in sync.
    isFullscreenFlow(){
      var path = this.$route && this.$route.path ? this.$route.path : '';
      if(path === '/login' || path === '/register' || path.indexOf('/reset') === 0) return true;
      if(path.indexOf('/invoice/') === 0)  return true;   // public tokenised invoice view
      if(path.indexOf('/claim/') === 0)    return true;   // corporate-account claim
      if(path.indexOf('/admin') === 0)     return true;   // admin console has its own nav
      return false;
    },
    showGlobalBell(){
      var path = this.$route && this.$route.path ? this.$route.path : '';
      if(path === '/dashboard') return false;
      if(path === '/' || path === '') return false;
      if(path === '/notifications' || path.indexOf('/notifications/') === 0) return false;
      if(this.isFullscreenFlow) return false;
      if(path.indexOf('/sell/create') === 0 || path.indexOf('/sell/edit') === 0) return false;
      if(path.indexOf('/sell/dashboard') === 0 || path.indexOf('/sell/products') === 0) return false;
      if(path.indexOf('/cart') === 0 || path.indexOf('/checkout') === 0) return false;
      return true;
    },
    showThemeToggle(){
      var path = this.$route && this.$route.path ? this.$route.path : '';
      if(path.indexOf('/print') !== -1) return false;
      return true;
    },
    showGlobalTabbar(){
      // Logged-in-only — every tab destination requires auth anyway.
      if(!this.loggedIn) return false;
      var path = this.$route && this.$route.path ? this.$route.path : '';
      // Dashboard ships its own; full-screen flows hide it.
      if(path === '/dashboard') return false;
      if(this.isFullscreenFlow) return false;
      return true;
    }
  },
  mounted(){
    var self = this;
    this.fetchUnread();
    this.pollTimer = setInterval(function(){
      if (document.visibilityState !== 'hidden') self.fetchUnread();
    }, 60000);
    window.addEventListener('focus', this.fetchUnread);
    window.addEventListener('flockr-notifications-changed', this.fetchUnread);
    this.themeObserver = new MutationObserver(function(){
      self.themeDark = document.documentElement.getAttribute('data-theme') === 'dark';
    });
    this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
  },
  beforeUnmount(){
    if (this.pollTimer) clearInterval(this.pollTimer);
    window.removeEventListener('focus', this.fetchUnread);
    window.removeEventListener('flockr-notifications-changed', this.fetchUnread);
    if (this.themeObserver) this.themeObserver.disconnect();
  },
  methods:{
    isActive(prefix){
      var p = this.$route && this.$route.path ? this.$route.path : '';
      if (prefix === '/dashboard') return p === '/dashboard' || p === '/';
      return p === prefix || p.indexOf(prefix + '/') === 0;
    },
    go(path){
      if (this.$route.path !== path) this.$router.push(path);
    },
    async fetchUnread(){
      var uid = sessionStorage.getItem('userId');
      if (!uid) { this.globalUnread = 0; return; }
      try {
        var r = await executeJson({ function: 'getNotifications', userId: uid, limit: 1 }, flocker);
        if (r.data && r.data.success) this.globalUnread = parseInt(r.data.data.unread || 0, 10) || 0;
      } catch (e) { /* silent */ }
    },
    goAndCloseMenu(path){
      this.showGlobalMenu = false;
      this.go(path);
    },
    toggleGlobalTheme(){
      var next = this.themeDark ? 'light' : 'dark';
      window.__flockrSetTheme(next);
      this.themeDark = next === 'dark';
    },
    logout(){
      this.showGlobalMenu = false;
      sessionStorage.clear();
      this.loggedInTick = false;
      this.$router.push('/login');
    }
  }
};
</script>

<!--
  GLOBAL MOBILE STYLESHEET — applies to every page in the portal.
  Targets the universal pain points that make a web app feel un-native on phones:
    * iOS auto-zoom when an input < 16px is focused
    * Horizontal overflow from a single overlong line
    * Pinch-zoom / double-tap-zoom flicker
    * Tap-highlight blue flash
    * Long-press text selection on tap targets
    * Modals / containers ignoring the iPhone notch + home indicator
  Page-specific files can still override scoped styles when they need to.
-->
<style>
/* Print rules (Alex 2026-07-22) — kill on-screen chrome so exported PDFs are
   clean: mobile tabbar, floating cart FAB, floating dev-menus, sticky headers,
   toast containers, drawer overlays. Any element that shouldn't appear on paper
   should either be marked .no-print or belong to one of these families. */
@media print {
  .glb-tabbar,
  .glb-menu-overlay,
  .dash-container-nav,
  .dash-topbar,
  .cart-fab,
  .fab,
  .sticky-nav,
  .no-print,
  .toast, .toast-container,
  nav.mobile-nav,
  .mobile-nav-container,
  .glb-theme-btn {
    display: none !important;
  }
  body { background: #FFFFFF !important; }
  /* Neutralise SPA layout scrollers so the browser paginates the doc */
  html, body { overflow: visible !important; height: auto !important; max-width: none !important; }
}

/* Width clamps go on body only — setting overflow-x:hidden on BOTH html and body
   breaks vertical scroll on Mac trackpads / some browsers (the browser stops
   knowing which element is the scroll root). */
html {
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;
}
body {
  width: 100%;
  max-width: 100vw;
  overflow-x: hidden;
  -webkit-tap-highlight-color: transparent;
}

/* touch-action: manipulation is a TAP-target speedup. Applying it to html/body
   breaks Mac trackpad scroll (Safari/Chrome route trackpad through touch).
   Scope it to actual tap targets instead. */
button, .btn, a, [role="button"], .dash-tab, .dash-icon-btn, .nb-btn {
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

/* iOS auto-zooms on focus when input font-size < 16px. Force 16px on mobile. */
@media (max-width: 768px) {
  input,
  textarea,
  select,
  button {
    font-size: 16px !important;
  }
  /* Tighten Bootstrap default padding on phones so content uses the screen */
  .container,
  .container-fluid {
    padding-left: 12px !important;
    padding-right: 12px !important;
  }
  /* Bootstrap row gutters tightened */
  .row.g-3,
  .row.g-4 {
    --bs-gutter-x: .6rem;
    --bs-gutter-y: .6rem;
  }
  /* Tables shouldn't blow horizontal — wrap them in a scroll container instead */
  table {
    max-width: 100%;
  }
  /* Modal overlays should respect safe-area on phones */
  .modal,
  .modal-dialog {
    margin: 0 !important;
    padding: env(safe-area-inset-top, 0px) 0 env(safe-area-inset-bottom, 0px);
  }
}

/* Prevent stray text selection on tap targets (mobile long-press selecting nav text) */
button, .btn, [role="button"], .dash-tab {
  -webkit-user-select: none;
  user-select: none;
}

/* ===== GLOBAL FLOATING THEME TOGGLE ===== */
.glb-theme-btn {
  position: fixed; left: 14px; bottom: 18px; z-index: 1050;
  width: 42px; height: 42px; border-radius: 50%;
  border: 1px solid var(--fm-br, #2A2520);
  background: var(--fm-card, #141414); color: var(--fm-h, #F5F0E8);
  font-size: 19px; display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 14px rgba(0,0,0,.3); cursor: pointer;
}
html[data-theme="light"] .glb-theme-btn { background: #fff; color: #3E3427; border-color: #E2DCD2; }
@media (max-width: 767px) {
  .glb-theme-btn { bottom: calc(78px + env(safe-area-inset-bottom, 0px)); }
}

/* ===== GLOBAL MOBILE BOTTOM TAB BAR ===== */
.glb-tabbar {
  position: fixed; left: 0; right: 0; bottom: 0; z-index: 1100;
  display: flex; justify-content: space-around; align-items: stretch;
  background: rgba(20,20,20,.96); backdrop-filter: blur(14px);
  border-top: 1px solid rgba(191,161,100,.18);
  padding: 6px 4px calc(6px + env(safe-area-inset-bottom, 0px));
}
html[data-theme="light"] .glb-tabbar { background: rgba(255,255,255,.97); border-top-color: #E8E2DA; }
.glb-tab {
  flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 2px; padding: 6px 4px; color: var(--fm-s, #6B6356);
  text-decoration: none; font-size: 10px; font-weight: 600; letter-spacing: .2px;
  border-radius: 10px; min-height: 50px; transition: all .15s;
  position: relative;
}
.glb-tab i { font-size: 22px; line-height: 1; }
.glb-tab.active { color: #BFA164; }
.glb-tab.active i { transform: scale(1.06); }
.glb-tab:active { background: rgba(191,161,100,.08); }
.glb-tab-badge {
  position: absolute; top: 2px; right: 22%;
  background: #FF6B6B; color: #FFF;
  font-size: 9px; font-weight: 700;
  padding: 1px 5px; border-radius: 10px; min-width: 16px; text-align: center;
}

/* Global menu bottom-sheet (mobile only) */
.glb-menu-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.55);
  display: flex; align-items: flex-end; justify-content: center;
  z-index: 1200;
}
.glb-menu-sheet {
  width: 100%; max-width: 520px;
  background: #141414; color: #F5F0E8;
  border-radius: 18px 18px 0 0;
  padding: 8px 0 calc(8px + env(safe-area-inset-bottom, 0px));
  max-height: 80vh; overflow-y: auto;
  box-shadow: 0 -10px 40px rgba(0,0,0,.45);
}
html[data-theme="light"] .glb-menu-sheet { background: #FFFFFF; color: #1A1A1A; }
.glb-menu-handle {
  width: 40px; height: 4px; border-radius: 2px;
  background: #3A352B; margin: 6px auto 10px;
}
html[data-theme="light"] .glb-menu-handle { background: #D8D0C4; }
.glb-menu-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 4px 18px 12px; border-bottom: 1px solid #2A2520;
  font-size: 13px; letter-spacing: .3px;
}
html[data-theme="light"] .glb-menu-head { border-bottom-color: #F0EBE4; }
.glb-menu-head strong { color: #BFA164; font-weight: 700; font-size: 14px; }
.glb-menu-close {
  background: transparent; border: none; color: #6B6356; font-size: 22px;
  width: 32px; height: 32px; border-radius: 50%; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
}
.glb-menu-item {
  display: flex; align-items: center; gap: 12px;
  padding: 13px 18px; color: #F5F0E8; text-decoration: none;
  font-size: 14px; transition: background .12s;
  border-bottom: 1px solid #1F1B16;
}
html[data-theme="light"] .glb-menu-item { color: #1A1A1A; border-bottom-color: #F5F1E8; }
.glb-menu-item i { font-size: 20px; color: #BFA164; width: 24px; text-align: center; }
.glb-menu-item:hover, .glb-menu-item:active { background: rgba(191,161,100,.08); }
.glb-menu-divider { height: 6px; background: transparent; }
.glb-menu-danger { color: #FF6B6B !important; }
.glb-menu-danger i { color: #FF6B6B !important; }
.glb-menu-pill {
  margin-left: auto; background: #FF6B6B; color: #FFF;
  font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 10px;
}
.glb-menu-fade-enter-active, .glb-menu-fade-leave-active {
  transition: opacity .2s ease;
}
.glb-menu-fade-enter-active .glb-menu-sheet, .glb-menu-fade-leave-active .glb-menu-sheet {
  transition: transform .25s cubic-bezier(.18,.89,.32,1.28);
}
.glb-menu-fade-enter-from, .glb-menu-fade-leave-to { opacity: 0; }
.glb-menu-fade-enter-from .glb-menu-sheet,
.glb-menu-fade-leave-to .glb-menu-sheet { transform: translateY(100%); }

/* Push page content above the tab bar on mobile so footers/last items don't hide.
   On desktop the bar isn't rendered, so the padding is a no-op. */
@media (max-width: 767.98px) {
  body { padding-bottom: calc(64px + env(safe-area-inset-bottom, 0px)); }
}

/* Top safe-area for status-bar height on iOS PWA installs / Safari fullscreen */
.dash-topbar,
.landing-nav,
header,
.topbar,
.nav-topbar {
  padding-top: env(safe-area-inset-top, 0px);
}

/* Make scrollable tables on mobile actually scroll horizontally */
@media (max-width: 768px) {
  .table-scroll-wrap,
  .table-responsive {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}

/* Smoother momentum scrolling on all scroll containers on iOS */
.overflow-auto,
.overflow-y-auto,
.overflow-x-auto {
  -webkit-overflow-scrolling: touch;
}

/* === Hide every scrollbar on mobile (vertical + horizontal, page + inner containers).
   Native apps don't show scrollbars — they convey scroll position by content motion.
   Scroll behavior itself stays intact; only the visual bar disappears. === */
@media (max-width: 768px) {
  html, body, * {
    scrollbar-width: none;       /* Firefox */
    -ms-overflow-style: none;    /* IE / old Edge */
  }
  html::-webkit-scrollbar,
  body::-webkit-scrollbar,
  *::-webkit-scrollbar {
    display: none;               /* Chrome, Safari, Edge */
    width: 0;
    height: 0;
  }
}
</style>
